export function normalizeSyncText(value) {
  return String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
}

export function getFunctionalHazardKey(row = {}) {
  return [
    row.fromFunction,
    row.controlAction,
    row.toFunction,
    row.subsystem || "Unallocated",
  ].map(normalizeSyncText).join("::");
}

export function hasFunctionalHazardKey(row = {}) {
  return [
    row.fromFunction,
    row.controlAction,
    row.toFunction,
  ].every((value) => normalizeSyncText(value));
}

function findHeaderIndex(headers = [], candidates = []) {
  const normalized = headers.map(normalizeSyncText);
  return candidates
    .map((candidate) => normalized.indexOf(normalizeSyncText(candidate)))
    .find((index) => index >= 0) ?? -1;
}

export function getHazardSummaryRowKey(row = [], headers = []) {
  const fromIdx = findHeaderIndex(headers, ["Function (From)", "From Function", "Source Function", "Controller"]);
  const actionIdx = findHeaderIndex(headers, ["Control Action", "Unsafe Control Action", "UCA", "Action", "What-If Scenario", "Failure Mode", "Malfunction"]);
  const toIdx = findHeaderIndex(headers, ["Function (To)", "To Function", "Target Function", "Controlled Process"]);
  const subsystemIdx = findHeaderIndex(headers, ["Subsystem Allocation", "Subsystem"]);
  return [
    fromIdx >= 0 ? row[fromIdx] : "",
    actionIdx >= 0 ? row[actionIdx] : "",
    toIdx >= 0 ? row[toIdx] : "",
    subsystemIdx >= 0 ? row[subsystemIdx] : "",
  ].map(normalizeSyncText).join("::");
}

function getHazardSummaryIdentityColumnIndexes(headers = []) {
  return new Set([
    findHeaderIndex(headers, ["Function (From)", "From Function", "Source Function", "Controller"]),
    findHeaderIndex(headers, ["Control Action", "Unsafe Control Action", "UCA", "Action", "What-If Scenario", "Failure Mode", "Malfunction"]),
    findHeaderIndex(headers, ["Function (To)", "To Function", "Target Function", "Controlled Process"]),
    findHeaderIndex(headers, ["Subsystem Allocation", "Subsystem"]),
  ].filter((index) => index >= 0));
}

function isHazardSummaryPlaceholderRow(row = [], headers = []) {
  const identityIndexes = getHazardSummaryIdentityColumnIndexes(headers);
  return row.every((cell, index) => (
    identityIndexes.has(index) || !normalizeSyncText(cell)
  ));
}

export function syncFunctionalRowsToHazardSummary({
  summary = null,
  functionalRows = [],
  headers = [],
  buildFallbackRow,
} = {}) {
  if (!Array.isArray(functionalRows) || !functionalRows.length || typeof buildFallbackRow !== "function") {
    return { summary, addedCount: 0, addedIndexes: [], removedCount: 0 };
  }
  const currentHeaders = Array.isArray(summary?.[0]) && summary[0].length ? summary[0] : headers;
  if (!Array.isArray(currentHeaders) || !currentHeaders.length) {
    return { summary, addedCount: 0, addedIndexes: [], removedCount: 0 };
  }
  const functionalKeys = new Set(
    functionalRows
      .filter(hasFunctionalHazardKey)
      .map(getFunctionalHazardKey)
      .filter((key) => key.replace(/:/g, ""))
  );
  const startingRows = Array.isArray(summary) ? summary.slice(1) : [];
  const rows = startingRows.filter((row) => {
    const rowKey = getHazardSummaryRowKey(row, currentHeaders);
    return functionalKeys.has(rowKey) || !isHazardSummaryPlaceholderRow(row, currentHeaders);
  });
  const removedCount = startingRows.length - rows.length;
  const existingKeys = new Set(
    rows.map((row) => getHazardSummaryRowKey(row, currentHeaders)).filter((key) => key.replace(/:/g, ""))
  );
  const addedRows = [];
  const addedIndexes = [];
  functionalRows.forEach((functionalRow, index) => {
    if (!hasFunctionalHazardKey(functionalRow)) return;
    const key = getFunctionalHazardKey(functionalRow);
    if (!key.replace(/:/g, "") || existingKeys.has(key)) return;
    existingKeys.add(key);
    addedRows.push(buildFallbackRow(functionalRow, currentHeaders));
    addedIndexes.push(index);
  });
  if (!addedRows.length && !removedCount) return { summary, addedCount: 0, addedIndexes: [], removedCount: 0 };
  return {
    summary: [currentHeaders, ...rows, ...addedRows],
    addedCount: addedRows.length,
    addedIndexes,
    removedCount,
  };
}

function cellAt(headers = [], row = [], candidates = []) {
  const idx = findHeaderIndex(headers, candidates);
  return idx >= 0 ? String(row[idx] ?? "").trim() : "";
}

function numberCellAt(headers = [], row = [], candidates = []) {
  const value = Number(cellAt(headers, row, candidates));
  return Number.isFinite(value) && value > 0 ? value : null;
}

function riskKeyFromSummaryRow(headers = [], row = []) {
  const hazard = cellAt(headers, row, ["Hazard", "Hazards", "Risk", "Risk Title"]);
  const action = cellAt(headers, row, ["Control Action", "Unsafe Control Action", "UCA", "Action", "What-If Scenario", "Failure Mode", "Malfunction"]);
  const from = cellAt(headers, row, ["Function (From)", "From Function", "Source Function", "Controller"]);
  const to = cellAt(headers, row, ["Function (To)", "To Function", "Target Function", "Controlled Process"]);
  const subsystem = cellAt(headers, row, ["Subsystem Allocation", "Subsystem"]);
  return [hazard, action, from, to, subsystem].map(normalizeSyncText).join("::");
}

function riskTitleFromSummaryRow(headers = [], row = [], index = 0) {
  return (
    cellAt(headers, row, ["Hazard", "Hazards", "Risk", "Risk Title"]) ||
    cellAt(headers, row, ["Unsafe Control Action", "UCA", "Failure Mode", "What-If Scenario", "Scenario", "Control Action"]) ||
    `Risk ${index + 1}`
  );
}

function riskGroupTitleFromSummaryRow(headers = [], row = [], index = 0) {
  return (
    cellAt(headers, row, ["Hazard", "Hazards", "Risk", "Risk Title", "Unsafe Condition", "Unsafe System State"]) ||
    cellAt(headers, row, ["Effect", "Consequence", "Mishap", "Functional Degradation / Loss"]) ||
    riskTitleFromSummaryRow(headers, row, index)
  );
}

function riskDescriptionFromSummaryRow(headers = [], row = []) {
  return (
    cellAt(headers, row, ["Hazard", "Hazards"]) ||
    cellAt(headers, row, ["Effect", "Consequence", "Mishap", "Functional Degradation / Loss"]) ||
    "—"
  );
}

function riskGroupKeyFromSummaryRow(headers = [], row = [], index = 0) {
  const title = riskGroupTitleFromSummaryRow(headers, row, index);
  if (normalizeSyncText(title)) return normalizeSyncText(title);
  return riskKeyFromSummaryRow(headers, row);
}

function mergeIndexes(left = [], right = []) {
  return Array.from(new Set([
    ...(Array.isArray(left) ? left : []),
    ...(Array.isArray(right) ? right : []),
  ].map(Number).filter((value) => Number.isFinite(value) && value > 0))).sort((a, b) => a - b);
}

function buildRiskIssueFromHazardGroup({ group, makeId }) {
  const first = group.rows[0] || {};
  const title = first.groupTitle || `Safety Issue ${group.sourceIndexes[0] || 1}`;
  const sourceIndexes = mergeIndexes([], group.sourceIndexes);
  const hazardSourceKeys = Array.from(new Set(group.rows.map((item) => item.sourceKey).filter(Boolean)));
  const actions = Array.from(new Set(group.rows.map((item) => item.action).filter(Boolean))).slice(0, 4);
  const endpoints = Array.from(new Set(group.rows.flatMap((item) => [item.from, item.to, item.subsystem]).filter(Boolean))).slice(0, 5);
  const description = first.description && first.description !== "—"
    ? first.description
    : [
        title,
        actions.length ? `Related control actions/failure modes: ${actions.join("; ")}.` : "",
        endpoints.length ? `Affected functions/subsystems: ${endpoints.join("; ")}.` : "",
      ].filter(Boolean).join(" ");
  return {
    id: makeId(),
    title,
    description: description || "—",
    likelihood: group.likelihood || 3,
    severity: group.severity || 3,
    status: "Open",
    owner: "",
    dueDate: "",
    tags: "",
    sourceIndex: sourceIndexes[0],
    sourceIndexes,
    hazardSourceKeys,
    reportStale: true,
  };
}

export function buildConsolidatedRiskRegisterFromSummary(summary = null, makeId = () => "") {
  if (!Array.isArray(summary?.[0]) || summary.length < 2) return [];
  const headers = summary[0];
  const groups = new Map();

  summary.slice(1).forEach((row, rowIndex) => {
    const sourceIndex = rowIndex + 1;
    const sourceKey = riskKeyFromSummaryRow(headers, row);
    if (!sourceKey.replace(/:/g, "")) return;
    const groupKey = riskGroupKeyFromSummaryRow(headers, row, rowIndex);
    if (!groupKey.replace(/:/g, "")) return;
    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        rows: [],
        sourceIndexes: [],
        likelihood: null,
        severity: null,
      });
    }
    const group = groups.get(groupKey);
    group.rows.push({
      row,
      sourceIndex,
      sourceKey,
      groupTitle: riskGroupTitleFromSummaryRow(headers, row, rowIndex),
      title: riskTitleFromSummaryRow(headers, row, rowIndex),
      description: riskDescriptionFromSummaryRow(headers, row),
      action: cellAt(headers, row, ["Control Action", "Unsafe Control Action", "UCA", "Action", "What-If Scenario", "Failure Mode", "Malfunction"]),
      from: cellAt(headers, row, ["Function (From)", "From Function", "Source Function", "Controller"]),
      to: cellAt(headers, row, ["Function (To)", "To Function", "Target Function", "Controlled Process"]),
      subsystem: cellAt(headers, row, ["Subsystem Allocation", "Subsystem"]),
    });
    group.sourceIndexes.push(sourceIndex);
    group.likelihood = Math.max(group.likelihood || 0, numberCellAt(headers, row, ["Likelihood", "Probability"]) || 3);
    group.severity = Math.max(group.severity || 0, numberCellAt(headers, row, ["Severity", "Impact"]) || 3);
  });

  return Array.from(groups.values()).map((group) => buildRiskIssueFromHazardGroup({ group, makeId }));
}

export function mergeRiskRegisterWithHazardSummary({
  riskRegister = [],
  summary = null,
  makeId = () => "",
} = {}) {
  if (!Array.isArray(summary?.[0]) || summary.length < 2) {
    return { riskRegister, affectedIds: [], addedCount: 0, mergedCount: 0 };
  }
  const nextRisks = (Array.isArray(riskRegister) ? riskRegister : []).map((risk) => ({ ...risk }));
  const affectedIds = new Set();
  let addedCount = 0;
  let mergedCount = 0;
  const groupedNewRisks = buildConsolidatedRiskRegisterFromSummary(summary, makeId);

  groupedNewRisks.forEach((newRisk) => {
    const sourceIndexes = mergeIndexes(newRisk.sourceIndexes, [newRisk.sourceIndex]);
    const sourceKeys = Array.isArray(newRisk.hazardSourceKeys) ? newRisk.hazardSourceKeys : [];
    const titleDescriptionKey = `${normalizeSyncText(newRisk.title)}::${normalizeSyncText(newRisk.description)}`;
    const existingIndex = nextRisks.findIndex((risk) => {
      const keys = Array.isArray(risk.hazardSourceKeys) ? risk.hazardSourceKeys : [];
      if (sourceKeys.some((sourceKey) => keys.includes(sourceKey))) return true;
      const existingSourceIndexes = mergeIndexes(risk.sourceIndexes, [risk.sourceIndex]);
      if (sourceIndexes.some((sourceIndex) => existingSourceIndexes.includes(sourceIndex))) return true;
      return `${normalizeSyncText(risk.title)}::${normalizeSyncText(risk.description)}` === titleDescriptionKey;
    });
    if (existingIndex >= 0) {
      const risk = nextRisks[existingIndex];
      const nextSourceIndexes = mergeIndexes(risk.sourceIndexes, [risk.sourceIndex, ...sourceIndexes]);
      const nextKeys = Array.from(new Set([...(Array.isArray(risk.hazardSourceKeys) ? risk.hazardSourceKeys : []), ...sourceKeys]));
      const changed = nextSourceIndexes.length !== mergeIndexes(risk.sourceIndexes, [risk.sourceIndex]).length
        || nextKeys.length !== (Array.isArray(risk.hazardSourceKeys) ? risk.hazardSourceKeys.length : 0);
      if (changed) {
        nextRisks[existingIndex] = {
          ...risk,
          sourceIndexes: nextSourceIndexes,
          sourceIndex: nextSourceIndexes[0],
          hazardSourceKeys: nextKeys,
          reportStale: true,
        };
        affectedIds.add(risk.id);
        mergedCount += 1;
      }
      return;
    }
    nextRisks.push(newRisk);
    affectedIds.add(newRisk.id);
    addedCount += 1;
  });

  return {
    riskRegister: nextRisks,
    affectedIds: Array.from(affectedIds),
    addedCount,
    mergedCount,
  };
}
